import {
	GoogleMap as GMap,
	MAP_PANE,
	OverlayViewF,
	PolylineF,
} from '@react-google-maps/api';
import { memo, useCallback, useEffect, useState } from 'react';
import type { Coordinates, MapProps } from './map.types';
import { standard } from './map-config';
import { useGoogleMapsLoader } from './use-google-maps-loader';

const containerStyle = {
	width: '100%',
	height: '100%',
};

// Brand map pin: black body with a yellow centre
const MarkerPin = () => (
	<svg
		width="36"
		height="36"
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		className="drop-shadow"
		role="img"
		aria-label="Location"
	>
		<path
			d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
			fill="#191919"
			stroke="#FFFFFF"
			strokeWidth="0.75"
			strokeLinejoin="round"
		/>
		<circle cx="12" cy="9" r="3" fill="#D4FF37" />
	</svg>
);

// @TODO: move this component to the UI kit package if we decide to use it in other projects
const GoogleMap = ({
	initialLocation,
	locations,
	route,
	zoom = 8,
}: MapProps) => {
	const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);

	const { isLoaded } = useGoogleMapsLoader();

	const onLoad = useCallback(function callback(map: google.maps.Map) {
		map.setCenter(initialLocation);
		map.setZoom(zoom);

		setGoogleMap(map);
	}, []);

	const onUnmount = useCallback(function callback() {
		setGoogleMap(null);
	}, []);

	useEffect(() => {
		if (!locations || locations.length === 0) {
			return;
		}

		if (googleMap) {
			const bounds = new google.maps.LatLngBounds();
			locations?.forEach((marker: Coordinates) => {
				bounds.extend(marker);
			});

			googleMap.fitBounds(bounds);
		}
	}, [googleMap, locations]);

	if (!isLoaded) {
		return null;
	}

	return (
		<GMap
			mapContainerStyle={containerStyle}
			onLoad={onLoad}
			onUnmount={onUnmount}
			options={{
				styles: standard,
				gestureHandling: 'greedy',
				streetViewControl: false,
				fullscreenControl: false,
				mapTypeControl: false,
				clickableIcons: false,
			}}
		>
			{locations?.length &&
				locations.map((mark: Coordinates, i: number) => (
					<OverlayViewF
						mapPaneName={MAP_PANE}
						key={i}
						position={mark}
						getPixelPositionOffset={(width, height) => ({
							x: -(width / 2),
							y: -height,
						})}
					>
						<MarkerPin />
					</OverlayViewF>
				))}

			{route?.length && (
				<PolylineF
					path={route}
					options={{
						strokeColor: '#191919',
						strokeWeight: 5,
					}}
				/>
			)}
		</GMap>
	);
};

export default memo(GoogleMap);
